import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ServiceIcon from '../../components/ServiceIcon';
import { servicesApi } from '../../api/servicesApi';

const FALLBACK = [
  { _id: 'cleaning', slug: 'cleaning', icon: 'cleaning', name: 'Cleaning', description: 'Home and apartment cleaning when you need an extra hand.' },
  { _id: 'dog-walking', slug: 'dog-walking', icon: 'dog-walking', name: 'Dog walking', description: 'Reliable local walks and pet visits.' },
  { _id: 'cooking', slug: 'cooking', icon: 'cooking', name: 'Home cooking', description: 'Meal preparation and practical kitchen help.' },
  { _id: 'gardening', slug: 'gardening', icon: 'gardening', name: 'Gardening', description: 'Mowing, weeding and everyday yard help.' },
];

const SERVICE_DESCRIPTIONS = {
  cleaning: 'Home cleaning, tidying and everyday household upkeep.',
  'cleaning (home / apartment)': 'Home cleaning, tidying and everyday household upkeep.',
  'dog-walking': 'Local dog walks and simple pet-care support.',
  'dog walking': 'Local dog walks and simple pet-care support.',
  cooking: 'Meal prep, simple cooking and practical kitchen help.',
  'home-cooking': 'Meal prep, simple cooking and practical kitchen help.',
  'home cooking': 'Meal prep, simple cooking and practical kitchen help.',
  'home cooking / meal prep': 'Meal prep, simple cooking and practical kitchen help.',
  gardening: 'Weeding, light garden maintenance and outdoor tidy-ups.',
  'gardening / yard help': 'Weeding, light garden maintenance and outdoor tidy-ups.',
  handyman: 'Small household jobs, assembly and basic odd jobs.',
  'handyman / odd jobs': 'Small household jobs, assembly and basic odd jobs.',
  'moving-help': 'Help lifting, carrying boxes and handling small moves.',
  'moving help': 'Help lifting, carrying boxes and handling small moves.',
  'moving help / lifting': 'Help lifting, carrying boxes and handling small moves.',
  laundry: 'Washing, folding and ironing clothes and household linen.',
  'laundry / ironing': 'Washing, folding and ironing clothes and household linen.',
  errands: 'Groceries, local pickups and everyday shopping errands.',
  'errands / shopping': 'Groceries, local pickups and everyday shopping errands.',
  'tech-help': 'Help with laptops, devices and everyday technology.',
  'tech help': 'Help with laptops, devices and everyday technology.',
};

const getServiceDescription = (service) => {
  const nameKey = String(service?.name || '').trim().toLowerCase();
  const slugKey = String(service?.slug || '').trim().toLowerCase();
  return SERVICE_DESCRIPTIONS[nameKey]
    || SERVICE_DESCRIPTIONS[slugKey]
    || service?.description
    || 'Everyday local help for the work you need done.';
};

const Services = () => {
  const [services, setServices] = useState(FALLBACK);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    servicesApi.list().then((data) => {
      if (active && Array.isArray(data) && data.length) setServices(data);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter((service) => `${service.name || ''} ${getServiceDescription(service)}`.toLowerCase().includes(q));
  }, [services, query]);

  return (
    <>
      <Header />
      <title>Giggs Services | Find Local Help</title>
      <main className="w-full overflow-x-hidden bg-white font-dm text-slate-800">
        <section className="relative overflow-hidden bg-gradient-to-br from-[#EAF8F0] via-white to-[#F3FBF6] px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="absolute -right-28 -top-28 h-72 w-72 rounded-full bg-emerald-200/35 blur-3xl" />
          <div className="relative mx-auto grid w-full max-w-[1500px] items-center gap-9 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-12">
            <div className="min-w-0">
              <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">Everyday local services</span>
              <h1 className="mb-0 mt-3 max-w-3xl font-nunito text-[clamp(2.25rem,5vw,4rem)] font-bold leading-[1.08] tracking-[-0.02em] text-slate-950">Find the local help that fits your day</h1>
              <p className="mb-0 mt-4 max-w-2xl text-base font-normal leading-7 text-slate-600 lg:text-lg">Browse the services available on Giggs, then choose a date and a Gigger who suits the job.</p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/book" className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-semibold text-white no-underline shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-lg">Book a service <ArrowRight size={17} /></Link>
                <Link to="/provider-register" className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3.5 text-sm font-semibold text-slate-700 no-underline ring-1 ring-inset ring-slate-200 transition-all duration-200 hover:-translate-y-0.5 hover:ring-green-300 hover:shadow-md">Become a Gigger</Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="col-span-2 overflow-hidden rounded-[1.75rem] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.12)] sm:col-span-1 sm:row-span-2">
                <video
                  src="/gif/cleaning.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Local cleaning service on Giggs"
                  className="h-full min-h-[240px] w-full object-cover sm:min-h-[390px]"
                />
              </div>
              <div className="overflow-hidden rounded-[1.5rem] bg-white shadow-[0_14px_35px_rgba(15,23,42,0.1)]">
                <video
                  src="/gif/laundry_iron.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Laundry and ironing service on Giggs"
                  className="aspect-[4/3] h-full w-full object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-[1.5rem] bg-white shadow-[0_14px_35px_rgba(15,23,42,0.1)]">
                <video
                  src="/gif/tech-help.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Grocery and errand runs on Giggs"
                  className="aspect-[4/3] h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-11 sm:px-6 sm:py-14 lg:px-8 2xl:px-10">
          <div className="mx-auto w-full max-w-[1500px]">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">Browse all services</span>
                <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.5vw,2.8rem)] font-bold tracking-[-0.015em] text-slate-950">Choose what you need help with</h2>
              </div>
              <label className="relative block w-full md:max-w-sm">
                <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search services..." className="box-border w-full rounded-xl border-0 bg-white py-3 pl-10 pr-4 text-sm font-normal text-slate-800 shadow-sm ring-1 ring-inset ring-slate-200 outline-none transition-all duration-200 placeholder:text-slate-400 hover:ring-slate-300 focus:ring-2 focus:ring-green-600" />
              </label>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((service) => {
                const comingSoon = service.status === 'Coming Soon' || service.comingSoon;
                const description = getServiceDescription(service);
                const body = (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-green-700"><ServiceIcon serviceId={service.icon || service.slug || service._id} size={21} /></span>
                      {comingSoon && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">Coming soon</span>}
                    </div>
                    <h3 className="mb-0 mt-5 font-nunito text-lg font-bold text-slate-900">{service.name}</h3>
                    <p title={description} className="mb-0 mt-2 truncate text-sm font-normal leading-6 text-slate-500">{description}</p>
                    <span className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${comingSoon ? 'text-slate-400' : 'text-green-700'}`}>{comingSoon ? 'Available soon' : 'Book this service'} {!comingSoon && <ArrowRight size={16} />}</span>
                  </>
                );
                const cls = "group rounded-2xl bg-white p-5 no-underline shadow-[0_8px_28px_rgba(15,23,42,0.055)] ring-1 ring-black/[0.045] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_38px_rgba(15,23,42,0.1)]";
                return comingSoon ? <article key={service._id || service.slug} className={cls}>{body}</article> : <Link key={service._id || service.slug} to={`/book?service=${service.slug || service._id}`} className={cls}>{body}</Link>;
              })}
            </div>

            {!filtered.length && <div className="mt-8 rounded-2xl bg-slate-50 px-5 py-10 text-center text-sm text-slate-500">No services match your search.</div>}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Services;
